import React from 'react';
import { motion } from 'framer-motion';
import { DESIGN_TOKENS } from '../../constants/design';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'scan';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const variants = {
  primary: 'bg-saavi-500 hover:bg-saavi-600 text-white shadow-lg',
  secondary: 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg',
  outline: 'border-2 border-gray-300 text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200',
  ghost: 'text-gray-600 hover:bg-gray-100',
  danger: 'bg-red-600 hover:bg-red-700 text-white shadow-lg',
  scan: 'bg-green-600 hover:bg-green-700 text-white shadow-lg border-green-600'
};

const sizes = {
  sm: 'h-10 sm:h-12 px-4 sm:px-6 text-sm sm:text-base',
  md: 'h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg',
  lg: 'h-14 sm:h-16 px-8 sm:px-10 text-lg sm:text-xl',
  xl: 'h-16 sm:h-20 px-10 sm:px-12 text-xl sm:text-2xl'
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  children,
  className = '',
  disabled,
  ...props
}) => {
  const baseClasses = `
    inline-flex items-center justify-center
    rounded-lg font-medium
    transition-all duration-200
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
    disabled:opacity-50 disabled:cursor-not-allowed
    active:scale-95
    touch-manipulation
    ${variants[variant]}
    ${sizes[size]}
    ${className}
  `.trim();

  const content = (
    <>
      {icon && !loading && iconPosition === 'left' && (
        <span className="mr-2 flex-shrink-0">{icon}</span>
      )}
      {loading ? 'Loading...' : children}
      {icon && !loading && iconPosition === 'right' && (
        <span className="ml-2 flex-shrink-0">{icon}</span>
      )}
    </>
  );

  return (
    <motion.button
      whileHover={{ scale: disabled || loading ? 1 : 1.02 }}
      whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
      className={baseClasses}
      disabled={disabled || loading}
      {...props}
    >
      {content}
    </motion.button>
  );
};